import { Skeleton } from "@/components/_components/skeleton";
import { Card, CardContent } from "@/components/_components/card";
import { Button } from "@/components/_components/button";
import * as api from "@/lib/api/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useState, useEffect, useRef } from "react";
import { Clock, Globe, BarChart3, Edit } from "lucide-react";
import { formatDateTime } from "@/lib/utils";
import { usePageHeader } from "@/contexts/page-header-context";
import { Badge } from "@/components/_components/badge";
import type { ComplianceCountryDto } from "@/lib/api/types";
import CreateComplianceCountryDialog from "./_components/CreateComplianceCountryDialog";
import DeleteComplianceCountryDialog from "./_components/DeleteComplianceCountryDialog";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";
import { ErrorDisplay } from "@/components/_components/error-display";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { useAuthorities } from "@/hooks/useAuthorities";

const ComplianceQuietHoursPage: React.FC = () => {
  return (
    <PageAccessGuard authority="ADMIN_COUNTRY_MGMT_VIEW">
      <ComplianceQuietHoursContent />
    </PageAccessGuard>
  );
};

const ComplianceQuietHoursContent: React.FC = () => {
  const { setHeaderData } = usePageHeader();
  const { handleError } = useErrorHandler();
  const { hasAuthority } = useAuthorities();
  const [countries, setCountries] = useState<ComplianceCountryDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown | null>(null);
  const hasFetchedRef = useRef(false);
  
  const canUpdate = hasAuthority('ADMIN_COUNTRY_MGMT_UPDATE');

  const fetchCountries = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.getAllComplianceCountries();
      setCountries(response.data || []);
    } catch (err: unknown) {
      setError(err);
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchCountries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set page header data
  useEffect(() => {
    setHeaderData({
      stats: {
        count: countries.length,
        label: 'country',
        icon: <BarChart3 className="h-4 w-4" />
      },
      actions: canUpdate ? (
        <CreateComplianceCountryDialog onSuccess={fetchCountries} />
      ) : undefined,
    });

    // Cleanup: clear header when component unmounts
    return () => {
      setHeaderData(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries.length, canUpdate]);

  const formatWeekends = (weekends: string | null | undefined) => {
    if (!weekends || typeof weekends !== 'string') return '-';
    return weekends.split(',').map(day => day.trim()).join(', ');
  };

  const formatTime = (time: string | null | undefined) => {
    if (!time || typeof time !== 'string') return '-';
    // Convert 24-hour format to 12-hour format
    const parts = time.split(':');
    if (parts.length < 2) return '-';
    const [hours, minutes] = parts;
    const hour = parseInt(hours);
    if (isNaN(hour)) return '-';
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="bg-background">
        <div className="w-full mx-auto px-2 sm:px-2 lg:px-2 py-4">
          <div className="space-y-4">
            <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <Skeleton className="h-64 w-full" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-background">
        <div className="w-full mx-auto px-2 sm:px-2 lg:px-2 py-4">
          <ErrorDisplay error={error} onRetry={fetchCountries} />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background">
      <div className="w-full mx-auto px-2 sm:px-2 lg:px-2 py-4">
        <div className="space-y-4">
          {countries.length === 0 ? (
            <Card className="shadow-md border-border bg-card hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 mx-auto mb-4 bg-muted/30 rounded-full flex items-center justify-center">
                    <Clock className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">No Countries Configured</h3>
                  <p className="text-muted-foreground mb-4">
                    {canUpdate 
                      ? 'Start by adding your first country with compliance and quiet hours settings.'
                      : 'No countries have been configured yet.'
                    }
                  </p>
                  {canUpdate && <CreateComplianceCountryDialog onSuccess={fetchCountries} />}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border border-border/50 bg-card backdrop-blur-sm">
              <CardContent className="p-4">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-2 bg-primary/10 rounded-lg transition-colors hover:bg-primary/20">
                    <Clock className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h2 className="text-xl font-bold text-foreground">
                      Compliance & Quiet Hours
                    </h2>
                    <Badge variant="secondary" className="text-xs font-medium">
                      {countries.length} {countries.length === 1 ? 'country' : 'countries'}
                    </Badge>
                  </div>
                </div>

                <div className="w-full">
                  <div className="overflow-hidden rounded-lg border border-border/50">
                    <Table>
                      <TableHeader className="sticky top-0 z-10 bg-muted/95 backdrop-blur supports-[backdrop-filter]:bg-muted/60">
                        <TableRow>
                          <TableHead className="font-semibold">Country</TableHead>
                          <TableHead className="font-semibold">Code</TableHead>
                          <TableHead className="font-semibold">ISD Code</TableHead>
                          <TableHead className="font-semibold">Working Hours</TableHead>
                          <TableHead className="font-semibold">Weekends</TableHead>
                          <TableHead className="font-semibold">Language</TableHead>
                          <TableHead className="font-semibold">Updated By</TableHead>
                          <TableHead className="font-semibold">Updated At</TableHead>
                          <TableHead className="font-semibold text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {countries.map((country) => (
                          <TableRow 
                            key={country.id}
                            className="transition-colors hover:bg-muted/50 cursor-pointer"
                          >
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <Globe className="h-4 w-4 text-muted-foreground" />
                                {country.countryName}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="font-mono">
                                {country.countryCode}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="font-mono">
                                {country.isdCode}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-sm">
                                <Clock className="h-3 w-3 text-muted-foreground" />
                                {formatTime(country.startWorkingHour)} - {formatTime(country.endWorkingHour)}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">
                              {formatWeekends(country.weekends || '')}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {country.language || 'Not specified'}
                              </Badge>
                            </TableCell>
                            <TableCell>{country.updatedBy || '-'}</TableCell>
                            <TableCell className="text-sm">
                              {country.updatedAt ? formatDateTime(country.updatedAt) : '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1 justify-center">
                                {canUpdate && (
                                  <CreateComplianceCountryDialog 
                                    country={country} 
                                    onSuccess={fetchCountries}
                                    trigger={
                                      <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600 transition-colors" 
                                        title="Edit Country"
                                      >
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                    }
                                  />
                                )}
                                {canUpdate && (
                                  <DeleteComplianceCountryDialog 
                                    country={country} 
                                    onSuccess={fetchCountries}
                                  />
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComplianceQuietHoursPage;